import React, { useState, useMemo } from 'react'

import { LinkOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import useBatchOperation from '@hooks/shared/useBatchOperation'
import { MultipleValue } from '@shared/table/Rules/component'
import FullDayRangePicker from '@components/shared/FullDayRangePicker'

import TableIndex from '@shared/table/TableIndex'
import type { TableService, SupportRule, TableAction } from '@shared/table/TableIndex'
import { Tag, Space, Tooltip } from 'antd'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

interface ShelfMovementService extends TableService {
  export: (ids: unknown[], language: string) => Promise<unknown>;
}

interface InventoryTypeService {
  fetchList: (params: Record<string, unknown>) => Promise<{ data: Array<{ id: string; name: string }> }>;
}

interface ShelfMovementsListProps {
  role?: 'admin' | 'operator';
  service: ShelfMovementService;
  inventoryTypeService?: InventoryTypeService;
  inboundId?: string | null;
  addJob?: (job: unknown) => void;
}

interface ShelfMovementRow {
  id: string;
  from_shelf_location?: string | null;
  to_shelf_location?: string | null;
  product_id?: string | null;
  product_name?: string | null;
  product_sku?: string | null;
  inventory_type_name?: string | null;
  batch?: string | null;
  expiration_date?: string | null;
  quantity?: number;
  source_type?: string | null;
  source_id?: string | null;
  source_name?: string | null;
  operator_name?: string | null;
  created_at?: string;
  [key: string]: unknown;
}

interface ShelfMovementsDefaultQuery {
  sorting_field: string;
  sorting_direction: string;
  inbound_id?: string;
  [key: string]: unknown;
}

const ShelfMovementsList: React.FC<ShelfMovementsListProps> = ({
  role = 'admin',
  service, // New
  inventoryTypeService, // New
  inboundId = null,
  addJob,
}) => {
  const { t, i18n } = useTranslation()
  const [lastModified, setLastModified] = useState(0)

  // 使用共用 batch operation hook
  const executeBatchOperation = useBatchOperation(service, addJob)

  const urlPrefix = role === 'operator' ? '/operator' : '/admin'

  const columns = useMemo(() => {
    const cols: unknown[] = [
      {
        name: '',
        sortable: false,
        width: '40px',
        wrap: true,
        persist: true,
        cell: ({ from_shelf_location, to_shelf_location }: ShelfMovementRow) => {
          if (from_shelf_location && to_shelf_location) {
            return (
              <Tooltip title={t('shared.shelf_movements.list.directions.relocation')}>
                <span style={{ color: '#1890ff', fontSize: 16, cursor: 'default' }}>●</span>
              </Tooltip>
            );
          } else if (from_shelf_location && !to_shelf_location) {
            return (
              <Tooltip title={t('shared.shelf_movements.list.directions.outbound')}>
                <span style={{ color: '#ff4d4f', fontSize: 16, cursor: 'default' }}>●</span>
              </Tooltip>
            );
          } else if (!from_shelf_location && to_shelf_location) {
            return (
              <Tooltip title={t('shared.shelf_movements.list.directions.inbound')}>
                <span style={{ color: '#52c41a', fontSize: 16, cursor: 'default' }}>●</span>
              </Tooltip>
            );
          }
          return <span className="text-muted">-</span>;
        },
      },
      {
        nameKey: 'shared.shelf_movements.list.columns.from_shelf',
        name: (
          <Space size={4}>
            {t('shared.shelf_movements.list.columns.from_shelf')}
            <Tooltip title={t('shared.shelf_movements.list.column_tooltips.from_shelf')}>
              <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
            </Tooltip>
          </Space>
        ),
        sortable: false,
        width: '120px',
        wrap: true,
        persist: true,
        cell: ({ from_shelf_location }: ShelfMovementRow) => from_shelf_location ? (
          <span style={{ color: '#1890ff' }}>{from_shelf_location}</span>
        ) : (
          <span className="text-muted">-</span>
        ),
      },
      {
        nameKey: 'shared.shelf_movements.list.columns.to_shelf',
        name: (
          <Space size={4}>
            {t('shared.shelf_movements.list.columns.to_shelf')}
            <Tooltip title={t('shared.shelf_movements.list.column_tooltips.to_shelf')}>
              <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
            </Tooltip>
          </Space>
        ),
        sortable: false,
        width: '120px',
        wrap: true,
        cell: ({ to_shelf_location }: ShelfMovementRow) => to_shelf_location ? (
          <span style={{ color: '#52c41a' }}>{to_shelf_location}</span>
        ) : (
          <span className="text-muted">-</span>
        ),
      },
      {
        nameKey: `shared.shelf_movements.list.columns.product`,
        sortable: false,
        width: '200px',
        wrap: true,
        cell: ({ product_id, product_name, product_sku }: ShelfMovementRow) => {
          if (!product_id) {
            return <span className="text-muted">-</span>;
          }

          return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <Link to={`/products/${product_id}`} style={{ wordBreak: 'break-word' }}>
                <div>
                  <div>{product_name || '-'}</div>
                  <div style={{ fontSize: '12px', color: '#999' }}>{product_sku || '-'}</div>
                </div>
              </Link>
              <a
                href={`${urlPrefix}/products/${product_id}`}
                target="_blank"
                onClick={(e) => e.stopPropagation()}
                style={{ flexShrink: 0, marginLeft: '8px' }} rel="noreferrer"
              >
                <LinkOutlined style={{ fontSize: '12px', color: '#999' }} />
              </a>
            </div>
          );
        },
      },
      {
        nameKey: `shared.shelf_movements.list.columns.inventory_type`,
        sortable: false,
        width: '100px',
        wrap: true,
        selector: ({ inventory_type_name }: ShelfMovementRow) => inventory_type_name || '-',
      },
      {
        nameKey: `shared.shelf_movements.list.columns.batch`,
        sortable: false,
        width: '120px',
        wrap: true,
        selector: ({ batch }: ShelfMovementRow) => batch || '-',
      },
      {
        nameKey: `shared.shelf_movements.list.columns.expiration_date`,
        sortable: false,
        width: '120px',
        wrap: true,
        selector: ({ expiration_date }: ShelfMovementRow) => expiration_date || '-',
      },
      {
        nameKey: 'shared.shelf_movements.list.columns.quantity',
        name: (
          <Space size={4}>
            {t('shared.shelf_movements.list.columns.quantity')}
            <Tooltip title={t('shared.shelf_movements.list.column_tooltips.quantity')}>
              <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
            </Tooltip>
          </Space>
        ),
        width: '80px',
        wrap: true,
        selector: ({ quantity }: ShelfMovementRow) => quantity || 0,
      },
      {
        nameKey: 'shared.shelf_movements.list.columns.source_document',
        name: (
          <Space size={4}>
            {t('shared.shelf_movements.list.columns.source_document')}
            <Tooltip title={t('shared.shelf_movements.list.column_tooltips.source_document')}>
              <QuestionCircleOutlined style={{ color: '#999', fontSize: 12 }} />
            </Tooltip>
          </Space>
        ),
        sortable: false,
        width: '200px',
        wrap: true,
        cell: ({ source_type, source_id, source_name }: ShelfMovementRow) => {
          if (!source_type || !source_name) {
            return <span className="text-muted">-</span>;
          }

          let linkPath: string;
          let tagColor: string;
          let typeLabel: string;

          switch (source_type) {
            case 'Inbound':
              linkPath = `/inbounds/${source_id}`;
              tagColor = 'blue';
              typeLabel = t('shared.shelf_movements.list.source_types.inbound');
              break;
            case 'PickingList':
              linkPath = `/picking_lists/${source_id}`;
              tagColor = 'purple';
              typeLabel = t('shared.shelf_movements.list.source_types.picking_list');
              break;
            case 'StockAdjustment':
              linkPath = `/stock_adjustments/${source_id}`;
              tagColor = 'gold';
              typeLabel = t('shared.shelf_movements.list.source_types.stock_adjustment');
              break;
            case 'Fulfillment':
              linkPath = `/fulfillments/${source_id}`;
              tagColor = 'green';
              typeLabel = t('shared.shelf_movements.list.source_types.fulfillment');
              break;
            case 'ReplenishmentOrder':
              linkPath = `/replenishment_orders/${source_id}`;
              tagColor = 'cyan';
              typeLabel = t('shared.shelf_movements.list.source_types.replenishment_order');
              break;
            default:
              return <span className="text-muted">-</span>;
          }

          return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <div>
                <div><Tag color={tagColor}>{typeLabel}</Tag></div>
                <div style={{ marginTop: '4px' }}>
                  <Link to={linkPath} style={{ wordBreak: 'break-word' }}>{source_name}</Link>
                </div>
              </div>
              <a
                href={`${urlPrefix}${linkPath}`}
                target="_blank"
                onClick={(e) => e.stopPropagation()}
                style={{ flexShrink: 0, marginLeft: '8px' }} rel="noreferrer"
              >
                <LinkOutlined style={{ fontSize: '12px', color: '#999' }} />
              </a>
            </div>
          );
        },
      },
      {
        nameKey: `shared.shelf_movements.list.columns.operator`,
        sortable: false,
        width: '100px',
        wrap: true,
        selector: ({ operator_name }: ShelfMovementRow) => operator_name || '-',
      },
      {
        nameKey: `common.created_at`,
        sortable: true,
        sortFieldName: 'shelf_movements.created_at',
        width: '160px',
        wrap: true,
        selector: ({ created_at }: ShelfMovementRow) => created_at,
      },
    ];

    return cols;
  }, [role, urlPrefix, t])

  const supportRules = useMemo(() => {
    const rules: SupportRule[] = [
      {
        label: t(`common.created_at`),
        component: FullDayRangePicker,
        inputName: 'shelf_movements.created_at',
        defaultPinned: true,
        tooltip: {
          title: t('shared.shelf_movements.list.filter_tooltips.created_at.title'),
          content: t('shared.shelf_movements.list.filter_tooltips.created_at.content'),
        },
      },
      {
        label: t(`shared.shelf_movements.list.filters.product_sku`),
        component: MultipleValue,
        inputName: 'products.sku',
        createable: true,
        defaultPinned: true,
        tooltip: {
          title: t('shared.shelf_movements.list.filter_tooltips.product_sku.title'),
          content: t('shared.shelf_movements.list.filter_tooltips.product_sku.content'),
        },
      },
      {
        label: t(`shared.shelf_movements.list.filters.inventory_type`),
        component: MultipleValue,
        inputName: 'inventories.inventory_type_id',
        createable: false,
        lazyLoad: {
          action: inventoryTypeService ? (params: Record<string, unknown>) => inventoryTypeService.fetchList(params) : undefined,
          transform: (data: { data: Array<{ id: string; name: string }> }) => data.data.map((type) => ({
            label: type.name,
            value: type.id,
          })),
        },
        tooltip: {
          title: t('shared.shelf_movements.list.filter_tooltips.inventory_type.title'),
          content: t('shared.shelf_movements.list.filter_tooltips.inventory_type.content'),
        },
      },
      {
        label: t(`shared.shelf_movements.list.filters.batch`),
        component: MultipleValue,
        inputName: 'inventories.batch',
        createable: true,
        tooltip: {
          title: t('shared.shelf_movements.list.filter_tooltips.batch.title'),
          content: t('shared.shelf_movements.list.filter_tooltips.batch.content'),
        },
      },
      {
        label: t(`shared.shelf_movements.list.filters.expiration_date`),
        component: FullDayRangePicker,
        inputName: 'inventories.expiration_date',
        tooltip: {
          title: t('shared.shelf_movements.list.filter_tooltips.expiration_date.title'),
          content: t('shared.shelf_movements.list.filter_tooltips.expiration_date.content'),
        },
      },
    ];

    // Only show inbound_name filter if not already filtered by inbound
    if (!inboundId) {
      rules.push({
        label: t(`shared.shelf_movements.list.filters.inbound_name`),
        component: MultipleValue,
        inputName: 'inbounds.name',
        createable: true,
        tooltip: {
          title: t('shared.shelf_movements.list.filter_tooltips.inbound_name.title'),
          content: t('shared.shelf_movements.list.filter_tooltips.inbound_name.content'),
        },
      });
    }

    rules.push(
      {
        label: t(`shared.shelf_movements.list.filters.picking_list_id`),
        component: MultipleValue,
        inputName: 'picking_list_id',
        createable: true,
        tooltip: {
          title: t('shared.shelf_movements.list.filter_tooltips.picking_list_id.title'),
          content: t('shared.shelf_movements.list.filter_tooltips.picking_list_id.content'),
        },
      },
      {
        label: t(`shared.shelf_movements.list.filters.order_name`),
        component: MultipleValue,
        inputName: 'orders.name',
        createable: true,
        tooltip: {
          title: t('shared.shelf_movements.list.filter_tooltips.order_name.title'),
          content: t('shared.shelf_movements.list.filter_tooltips.order_name.content'),
        },
      }
    );

    return rules;
  }, [t, inboundId, inventoryTypeService])

  // Build default query with inbound_id filter if provided
  const defaultQuery = useMemo<ShelfMovementsDefaultQuery>(() => {
    const query: ShelfMovementsDefaultQuery = {
      sorting_field: 'shelf_movements.id',
      sorting_direction: 'desc',
    };

    if (inboundId) {
      query.inbound_id = inboundId;
    }

    return query;
  }, [inboundId]);

  const actions = useMemo<TableAction[]>(() => [
    {
      name: 'common.button.export',
      className: 'btn btn-primary btn-outline mb-0',
      do: (selected: any) => executeBatchOperation({
        task: () => service.export(Array.from(selected), i18n.language),
        jobName: 'common.button.export'
      })
    } as TableAction
  ], [executeBatchOperation, service, i18n])

  return (
    <TableIndex
      columns={columns}
      searchable={{
        match: 'contains',
        searchFields: [
          t('models.product.sku'),
          t('models.product.name'),
          t('shared.shelf_movements.list.columns.from_shelf'),
          t('shared.shelf_movements.list.columns.to_shelf'),
        ],
      }}
      columnsEditable={true}
      tableId={`${role === 'operator' ? 'Operator' : 'Admin'}ShelfMovementsTable`}
      defaultQuery={defaultQuery}
      supportRules={supportRules}
      lastModified={lastModified}
      setLastModified={setLastModified}
      actions={actions}
      service={service}
      filterHelpExample={t('shared.shelf_movements.list.filter_help_example') as string}
      filterDocsUrl={`/docs/${role}/shelf_movements/list#features`}
    />
  )
}

export default ShelfMovementsList
